import RangeFix from 'rangefix';
import * as React from 'react';
import { useWindowDimensions } from 'react-native';

import { Popover, type PopoverRef, PortalAbsolute, usePaletteHandle } from '@gitbook/react-uikit';
import type { TextRange, Value } from '@gitbook/slate';
import { type EditorContainer, isElementInEditor, useEditor } from '@gitbook/slate-react';
import { useDebouncedFlush, useLiveRef } from '@gitbook/util-react-hooks';

import { clearSelectionHighlighting } from '../common';
import { FormatRangeMenu } from './FormatRangeMenu';

interface Position {
    top: number;
    left: number;
    height: number;
    width: number;
}

export type HoveringMenuProps = {
    value: Value;
    selection: TextRange;
};
/*
 * Hover menu to format text.
 */
export function HoveringMenu(props: HoveringMenuProps): React.ReactElement | null {
    const { value, selection } = props;

    const editor = useEditor();

    const [position, setPosition] = React.useState<Position | null>(null);
    const rangeRef = React.useRef(null);
    const latestPositionRef = useLiveRef(position);
    const popoverRef = React.useRef<PopoverRef>(null);

    const palette = usePaletteHandle(rangeRef, {
        onClose: () => {
            editor.change((change) => {
                clearSelectionHighlighting(change);
                change.focus();
            });
        },
    });

    const { width, height } = useWindowDimensions();

    const isInSupportedBlock = !value.isInVoid;
    const opened = Boolean(
        // don't render if we are in a block that doesn't support it
        isInSupportedBlock
    );
    const visible = opened && position;
    const showFormatRangeMenu = isInSupportedBlock && selection.isFocused && selection.isExpanded;

    /*
     * Delay before updating position
     */
    const debounce = useDebouncedFlush(50);

    /*
     * Update position when selection is moving.
     */
    React.useEffect(() => {
        if (!opened || palette.opened) {
            // we don't want to update the position when the palette is still opened
            // otherwise we could risk unmounting this component and having an
            // invisible palette (see MVR-952)
            return;
        }

        if (selection.isCollapsed || selection.isUnset || !selection.isFocused) {
            setPosition(null);
            return;
        }

        debounce(() => {
            setPosition(locateNativeSelection(editor));
        });
    }, [selection, debounce, editor, opened, palette.opened]);

    React.useEffect(() => {
        if (!opened) {
            return;
        }

        const onScroll = () => {
            if (palette.opened) {
                // when opened, the palette is the only element that can scroll
                // we don't want to update the position when the palette is still opened
                // otherwise we could risk unmounting this component and having an
                // invisible palette (see MVR-952)
                return;
            }

            // Hide the menu when scrolling
            if (latestPositionRef.current) {
                setPosition(null);
            }

            // Update position when scrolling
            debounce(() => {
                setPosition(locateNativeSelection(editor));
            });
        };

        window.addEventListener('scroll', onScroll, true);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [editor, palette, opened, latestPositionRef, debounce]);

    // update position when window resizes
    React.useEffect(() => {
        debounce(() => {
            setPosition(locateNativeSelection(editor));
        });
    }, [width, height, debounce, editor]);

    React.useEffect(() => {
        if (position) {
            popoverRef.current?.updatePosition();
        }
    }, [position]);

    if (!visible) {
        return null;
    }

    return (
        <PortalAbsolute>
            {showFormatRangeMenu ? (
                <FormatRangePopover position={position} popoverRef={popoverRef} rangeRef={rangeRef}>
                    <FormatRangeMenu value={value} editor={editor} palette={palette} />
                </FormatRangePopover>
            ) : null}
        </PortalAbsolute>
    );
}

function FormatRangePopover(props: {
    children: React.ReactNode;
    position: Position;
    popoverRef: React.RefObject<PopoverRef>;
    rangeRef: React.RefObject<HTMLDivElement>;
}) {
    const { children, popoverRef, position, rangeRef } = props;
    return (
        <div
            ref={rangeRef}
            style={position}
            data-hide-on-drag
            className="pointer-events-none absolute z-50"
        >
            <Popover ref={popoverRef} kind="menu" preferredPlacement="top" chromeless>
                {children}
            </Popover>
        </div>
    );
}

/**
 * Locate the native selection on the screen.
 */
function locateNativeSelection(editor: EditorContainer): Position | null {
    const nativeSelection = window.getSelection();
    if (!nativeSelection || nativeSelection.rangeCount < 1) {
        return null;
    }

    const range = nativeSelection.getRangeAt(0);

    const rect = RangeFix.getBoundingClientRect(range);
    if (!rect) {
        return null;
    }

    const anchorNode = nativeSelection.anchorNode;

    if (!anchorNode || !isElementInEditor(editor, anchorNode)) {
        return null;
    }
    const scrollParent = anchorNode ? getScrollParent(anchorNode) : null;

    // Mimimum width for the selection wrapper
    const MIN_SELECTION_WIDTH = 15;

    const newSelection: Position = {
        top: rect.top + window.pageYOffset,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
    };

    if (newSelection.width < MIN_SELECTION_WIDTH) {
        newSelection.left -= (MIN_SELECTION_WIDTH - newSelection.width) / 2;
        newSelection.width = MIN_SELECTION_WIDTH;
    }

    // Hide the selection if the element is not visible in the viewport
    if (scrollParent) {
        const scrollParentRect = scrollParent.getBoundingClientRect();
        if (newSelection.top < scrollParentRect.top) {
            return null;
        }
        if (newSelection.top + newSelection.height > scrollParentRect.bottom) {
            return null;
        }
    }
    return newSelection;
}

/**
 * Return the first scrollable ancestor of the given node.
 */
function getScrollParent(node: Node): HTMLElement | null {
    if (node instanceof HTMLElement) {
        const styles = getComputedStyle(node, null);
        if (
            styles.getPropertyValue('overflow') === 'auto' ||
            styles.getPropertyValue('overflow') === 'scroll' ||
            styles.getPropertyValue('overflow-y') === 'auto' ||
            styles.getPropertyValue('overflow-y') === 'scroll'
        ) {
            return node;
        }
    }

    if (node.parentNode) {
        return getScrollParent(node.parentNode);
    }

    return null;
}
